import { EventBus, useGameStore, useLocalBubbleId, useProximityCount } from '@campus/core';
import { Icon } from '../ds/index.js';

/**
 * AdminCallPill — pastille « En discussion » dans l'administration.
 *
 * Le campus reste connecté derrière l'administration (voir App.tsx) : si
 * l'avatar est dans une bulle ou près de quelqu'un, la discussion continue et
 * le micro reste ouvert. Cette pastille le rappelle et permet de couper micro,
 * caméra et partage d'écran sans revenir au campus (mêmes événements que la
 * barre du jeu, traités par GameBridge).
 */
export function AdminCallPill() {
  const liveKitConnected = useGameStore((s) => s.liveKitConnected);
  const isLocalMuted     = useGameStore((s) => s.isLocalMuted);
  const isLocalCameraOff = useGameStore((s) => s.isLocalCameraOff);
  const isScreenSharing  = useGameStore((s) => s.isScreenSharing);
  const mutedUntil       = useGameStore((s) => s.mutedUntil);
  const setAdminPage     = useGameStore((s) => s.setAdminPage);
  const localBubbleId    = useLocalBubbleId();
  const proximityCount   = useProximityCount();

  if (!liveKitConnected || (!localBubbleId && proximityCount === 0)) return null;

  const mutedByAdmin = !!mutedUntil && mutedUntil > Date.now();
  const micOff = isLocalMuted || mutedByAdmin;
  const others = proximityCount === 1 ? '1 personne' : `${proximityCount} personnes`;

  return (
    <div
      role="region"
      aria-label="Discussion en cours"
      className="pl-hud"
      style={{ position: 'fixed', left: 16, bottom: 16, zIndex: 1000, gap: 4 }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0 10px', fontSize: 13 }}>
        <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-status-online)' }} />
        En discussion{proximityCount > 0 && ` · ${others}`}
      </span>
      <span className="pl-hud-sep" />
      <button
        type="button"
        className="pl-hud-btn"
        aria-pressed={!micOff}
        disabled={mutedByAdmin}
        title={mutedByAdmin ? 'Micro coupé par un admin' : micOff ? 'Activer le micro' : 'Couper le micro'}
        onClick={() => EventBus.emit('ui:toggle-mic', {})}
        style={micOff ? { color: 'var(--color-danger)' } : undefined}
      >
        <Icon name={micOff ? 'mic-off' : 'mic'} size={16} />{micOff ? 'Micro coupé' : 'Micro'}
      </button>
      <button
        type="button"
        className="pl-hud-btn"
        aria-pressed={!isLocalCameraOff}
        title={isLocalCameraOff ? 'Activer la caméra' : 'Couper la caméra'}
        onClick={() => EventBus.emit('ui:toggle-camera', {})}
        style={isLocalCameraOff ? { color: 'var(--color-text-secondary)' } : undefined}
      >
        <Icon name={isLocalCameraOff ? 'video-off' : 'video'} size={16} />Caméra
      </button>
      {isScreenSharing && (
        <button type="button" className="pl-hud-btn" onClick={() => EventBus.emit('ui:toggle-screenshare', {})}>
          <Icon name="monitor" size={16} />Arrêter le partage
        </button>
      )}
      <span className="pl-hud-sep" />
      <button type="button" className="pl-hud-btn" onClick={() => setAdminPage(null)}>
        <Icon name="arrow-left" size={16} />Revenir au campus
      </button>
    </div>
  );
}
